import React, {useEffect, useState} from 'react';
import {debounce} from 'lodash';
import {Config, Data, Layout} from 'plotly.js';
import PlotlyComponent from "react-plotly.js";

const Plot = PlotlyComponent as any;

export interface IScatterChartProps {
  id?: string;
  data: Array<{
    x: number;
    [key: string]: number;
  }>;
  seriesX: { name: string; max: number };
  seriesY: { name: string; max: number };
  downloadName?: string;
  xAxisInterval: number
  yAxisInterval: number
}

const ScatterPlot: React.FC<IScatterChartProps> = ({
    id,
    data,
    seriesX,
    seriesY,
    downloadName,
    xAxisInterval,
    yAxisInterval
  }) => {
  const [loading, setLoading] = useState(false);
  const updateChartData = debounce(() => {
    setLoading(true);
    setTimeout(() => setLoading(false), 100);
  }, 50);

  useEffect(() => {
    updateChartData();
  }, [data]);

  const transformData = (): Partial<Data>[] => {
    const allX: number[] = [];
    const allY: number[] = [];

    data.forEach(item => {
      const yKeys = Object.keys(item)
        .filter(key => key.startsWith('y'))
        .sort((a, b) => {
          const numA = parseInt(a.replace('y', ''));
          const numB = parseInt(b.replace('y', ''));
          return numA - numB;
        });

      yKeys.forEach(yKey => {
        if (item[yKey] !== undefined && item[yKey] !== null) {
          allX.push(item.x);
          allY.push(item[yKey]);
        }
      });
    });

    return [{
      x: allX,
      y: allY,
      mode: 'markers',
      type: 'scatter',
      marker: {
        size: 3,
        color: '#59d6ff',
        opacity: 1,
        symbol: 'circle'
      },
      hoverinfo: 'x+y',
      hovertemplate: 'x: %{x}<br>y: %{y}<extra></extra>',
      showlegend: false
    }];
  };
  const layout: Partial<Layout> = {
    autosize: true,
    margin: {l: 50, r: 30, t: 30, b: 50},
    xaxis: {
      title: {text: seriesX.name},
      range: [0, seriesX.max],
      autorange: true,
      tickfont: {size: 10},
      gridcolor: '#e5e5e5',
      zeroline: false,
      dtick: xAxisInterval
    },
    yaxis: {
      title: {text: seriesY.name},
      range: [0, seriesY.max],
      tickfont: {size: 10},
      gridcolor: '#e5e5e5',
      zeroline: false,
      autorange: true,
      dtick: yAxisInterval
    },
    plot_bgcolor: 'white',
    paper_bgcolor: 'white',
    hovermode: 'closest' as const
  };

  const config: Partial<Config> = {
    displayModeBar: true,
    responsive: true,
    displaylogo: false,
    toImageButtonOptions: {
      format: 'jpeg' as const,
      filename: downloadName || 'scatter-plot',
      height: 800,
      width: 1200,
      scale: 1
    },
    scrollZoom: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d']
  };
  return (
    <div id={id} style={{width: '100%', height: '100%', position: 'relative'}}>
      {loading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1
        }}>
          Loading...
        </div>
      )}
      <Plot
        data={transformData()}
        layout={layout}
        useResizeHandler
        style={{width: '100%', height: '100%'}}
        config={config}
      />
    </div>
  );
};

export default ScatterPlot;
